/**
 * Rental DTO + related lookups exposed by rental-service `/api/rentals`.
 * Mirrors `net.nawam.rental.model.entity.Rental` filtered by `RenRead` /
 * `RenReadDetail` JsonViews.
 */

import type { UserRef } from './user.types';
import type { DocusignStatus, Image, Vehicle } from './vehicle.types';

/** All possible rental workflow statuses (see `RentalStatusEnum`). */
export type RentalStatus =
  | 'PREPENDING'
  | 'PENDING'
  | 'CONFIRMED'
  | 'STARTED'
  | 'FINISHED'
  | 'CANCELLED';

/** Customer (renter) reference — minimal subset useful for admin display. */
export interface CustomerRef {
  id: number;
  userId?: number | null;
  /** Read-model du user lié (firstName, lastName, email…). */
  user?: UserRef | null;
  licenseNumber?: string | null;
  licenseExpiryDate?: string | null;
  licenseIssuingCountry?: string | null;
  licenseIssuingState?: string | null;
  dateOfBirth?: string | null;
  address?: string | null;
  city?: string | null;
  postalCode?: string | null;
  country?: string | null;
  emergencyContactName?: string | null;
  emergencyContactPhone?: string | null;
  emergencyContactRelation?: string | null;
  verificationStatus?: 'PENDING' | 'VERIFIED' | 'REJECTED' | null;
  verifiedAt?: string | null;
  verifiedBy?: number | null;
  rejectionReason?: string | null;
  totalRentals?: number;
  averageRating?: number | null;
}

export interface Rental {
  // ── BaseEntity ───────────────────────────────────────────────────────
  id: number;
  createdAt?: string | null;
  updatedAt?: string | null;

  // ── parties ───────────────────────────────────────────────────────────
  /** Deprecated. Use `customer.userId` instead. */
  userId?: number | null;
  customer?: CustomerRef | null;
  vehicle: Vehicle | null;

  // ── workflow ──────────────────────────────────────────────────────────
  rentalStatusEnum: RentalStatus;

  // ── dates & cost ──────────────────────────────────────────────────────
  startDate: string;
  endDate: string;
  nbHours: number;
  pickupLocation: string;
  returnLocation: string;
  totalCost: number;

  // ── contract / signatures ─────────────────────────────────────────────
  qrcode?: string | null;
  docusignSubmissionStatus?: DocusignStatus | null;
  docusignSubmissionId?: number | null;
  docusignSubmissionLink?: string | null;
  rentalContract?: string | null;

  // ── post-rental ───────────────────────────────────────────────────────
  averageRating?: number | null;
  lateFee?: number | null;
  actualReturnDate?: string | null;

  // ── photos (detail) ───────────────────────────────────────────────────
  photosBefore?: Image[];
  photosAfter?: Image[];
}

/**
 * Détail de remboursement renvoyé par `POST /rental/api/rentals/{id}/cancel`
 * (le back renvoie ce Map, PAS un `Rental`). Calculé par la politique
 * d'annulation applicable (override véhicule sinon globale). Cf.
 * `docs/CANCELLATION_POLICY_STATUS.md`.
 */
export interface CancellationRefund {
  /** Nom de la politique appliquée (ex. "Flexible", "Standard"). */
  policyName?: string | null;
  policyDescription?: string | null;
  /** Heures entre l'instant de l'annulation et le début prévu. */
  hoursBeforeStart?: number | null;
  /** Pourcentage remboursé (0 / partiel / 100). */
  refundPercentage?: number | null;
  /** Coût total initial de la location. */
  originalAmount?: number | null;
  /** Frais fixe d'annulation prélevé par la politique. */
  cancellationFee?: number | null;
  /** Montant effectivement remboursé au client. */
  refundAmount?: number | null;
  canCancel?: boolean | null;
  /** Présent seulement si `refundAmount == 0`. */
  warning?: string | null;
  // ── champs ajoutés par `cancelRentalMe` ───────────────────────────────
  rentalId?: number | null;
  vehicleName?: string | null;
  startDate?: string | null;
  endDate?: string | null;
}

import { defensiveStatusMeta } from '../helpers/status-meta';

/** UI helper : maps a rental status to its label + Tailwind palette suffix.
 *  Defensive: unknown/null statuses fall back to `{label:'—', palette:'gray'}`. */
export const RENTAL_STATUS_META = defensiveStatusMeta<RentalStatus>({
  PREPENDING: { label: 'status.rental.PREPENDING', palette: 'gray' },
  PENDING: { label: 'status.rental.PENDING', palette: 'warning' },
  CONFIRMED: { label: 'status.rental.CONFIRMED', palette: 'brand' },
  STARTED: { label: 'status.rental.STARTED', palette: 'blue-light' },
  FINISHED: { label: 'status.rental.FINISHED', palette: 'gray' },
  CANCELLED: { label: 'status.rental.CANCELLED', palette: 'error' },
});
